import React, { useEffect, useRef, useState } from 'react'
import propTypes from 'prop-types'
import Quill from 'quill'
import ColorPicker from '../../ColorPicker'
import DeleteItemDialogue from '../../DeleteItemDialogue'
import { updateText } from '../../../store/texts/textAction'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { replaceSpacesWithNbsp } from '../../../utils'
import labelAlignmentPicker from '../../../labelAlignmentPicker'
import accessibleEditorName from '../../../accessibleEditorName'

const Delta = Quill.import('delta')
const LinkBlot = Quill.import('formats/link')

// Custom toiminto, joka lisää teksteissä linkeille title-attribuutin
const originalLinkCreate = LinkBlot.create
LinkBlot.create = function create(value) {
  const node = originalLinkCreate.call(this, value)
  const href = node?.getAttribute('href')
  if (href) {
    node.setAttribute('title', href)
  } else {
    node.removeAttribute('title')
  }
  return node
}
Quill.register(LinkBlot, true)

// Quill: Laajennetaan size-formaattia tukemaan tarvittavia CSS-luokkia
const SizeClass = Quill.import('attributors/class/size')
SizeClass.whitelist = ['small', false, 'large', 'huge', 'mega']
Quill.register(SizeClass, true)

const TextEditor = ({
  textbox,
  parent,
  editorContent,
  setEditorContent,
  setDirty,
  saveText,
  getFontSize,
  // dragging,
  width,
  setOpenLinkUrlPrompt,
  topAlignFontMenu,
}) => {
  const editorRef = useRef()
  const quillRef = useRef()
  const [openDeleteContentModal, setOpenDeleteContentModal] = useState(false)
  const [openColorModal, setOpenColorModal] = useState(false)
  const [changeFont, setChangeFont] = useState(false)
  const [fontColor, setFontColor] = useState('black')
  const [backgroundColor, setBackgroundColor] = useState(
    textbox && textbox.bgColor ? textbox.bgColor : 'transparent'
  )
  const [undoLevelCount, setUndoLevelCount] = useState(0)
  const [toolbarVisible, setToolbarVisible] = useState(false)
  // const [persistToolbar, setPersistToolbar] = useState(false)
  const dispatch = useDispatch()
  const textIds = useSelector((state) => {
    const page = state.pages.pages[state.pages.currentPage]
    return page?.texts
  })
  const editorName = accessibleEditorName('Kirjan teksti', textIds, textbox?.id)
  const suppressPlainPasteRef = useRef(false)

  const plainTextClipboardMatcher = (node, delta) => {
    if (suppressPlainPasteRef.current) {
      return delta
    }
    const plaintext = node?.textContent ?? ''
    return new Delta().insert(plaintext)
  }

  useEffect(() => {
    if (!quillRef.current && editorRef.current) {
      const quill = new Quill(editorRef.current, {
        theme: 'snow',
        modules: {
          toolbar: `#toolbar-${textbox.id}`,
          history: {
            delay: 1500,
            maxStack: 500,
            userOnly: false,
          },
          clipboard: {
            matchers: [[Node.ELEMENT_NODE, plainTextClipboardMatcher]],
          },
        },
        placeholder: 'Kirjoita tähän...',
      })
      quill.root.setAttribute('spellcheck', false)
      quill.root.setAttribute('aria-label', editorName)
      labelAlignmentPicker(document.getElementById(`toolbar-${textbox.id}`))
      quillRef.current = quill
    }
  }, [textbox.id])

  useEffect(() => {
    const root = quillRef.current?.root
    if (!root) return
    root.setAttribute('aria-label', editorName)
  }, [editorName])

  useEffect(() => {
    if (
      quillRef.current &&
      editorContent &&
      quillRef.current.getText().trim() === ''
    ) {
      const modifiedContent = replaceSpacesWithNbsp(editorContent)
      suppressPlainPasteRef.current = true
      try {
        quillRef.current.clipboard.dangerouslyPasteHTML(modifiedContent)
      } finally {
        suppressPlainPasteRef.current = false
      }
    }
  }, [editorContent])

  useEffect(() => {
    const editorElement = editorRef.current
    if (editorElement) {
      const quillEditor = quillRef.current

      const handleFocus = () => {
        setToolbarVisible(true)
      }

      const handleBlur = (event) => {
        // Tarkista, onko blur-tapahtuman kohde työkalupalkin sisällä
        if (!event.relatedTarget || !event.relatedTarget.closest('.ql-toolbar')) {
          setToolbarVisible(false)
        }
      }

      const handleTextChange = () => {
        const html = quillEditor.root.innerHTML
        const htmlFixedSpaces = html.replace(/ {2}/g, '&nbsp;&nbsp;')
        if (editorContent !== htmlFixedSpaces) {
          setEditorContent(htmlFixedSpaces)
          setDirty(true)
        }
      }

      quillEditor.root.addEventListener('focus', handleFocus)
      quillEditor.root.addEventListener('blur', handleBlur, true)
      quillEditor.on('text-change', handleTextChange)

      return () => {
        quillEditor.root.removeEventListener('focus', handleFocus)
        quillEditor.root.removeEventListener('blur', handleBlur)
        quillEditor.off('text-change', handleTextChange)
      }
    }
  }, [quillRef.current])

  // Aseta fokus uudelle editorille, jos sisältö on tyhjä (uusi tekstilaatikko)
  useEffect(() => {
    if (quillRef.current) {
      const quillEditor = quillRef.current
      const isEmpty =
        !editorContent ||
        editorContent.trim() === '' ||
        quillEditor.getText().trim() === '' ||
        quillEditor.root.innerHTML === '<p></p>'

      if (isEmpty) {
        // Käytetään setTimeout:ia varmistaaksemme että DOM on valmis
        setTimeout(() => {
          quillEditor.focus()
        }, 0)
      }
    }
  }, [quillRef.current, textbox.id])

  // useEffect(() => {
  //   if (editorRef && editorRef.current && dragging) {
  //     setPersistToolbar(true)
  //     editorRef.current.ui.hide()
  //   } else if (editorRef && editorRef.current) {
  //     setPersistToolbar(false)
  //   }
  // }, [dragging])

  const emptyContents = () => {
    setDirty(true)
    saveText(null)
    if (quillRef.current) {
      quillRef.current.setText('')
    }
  }

  const changeFontColor = () => {
    setOpenColorModal(true)
    setChangeFont(true)
  }

  useEffect(() => {
    const quill = quillRef.current
    if (changeFont && quill) {
      quill.focus()
      quill.format('color', fontColor)
      setUndoLevelCount(undoLevelCount + 1)
    }
  }, [fontColor])

  useEffect(() => {
    if (!changeFont && editorRef && editorRef.current && editorRef.current.getBody) {
      editorRef.current.getBody().style.backgroundColor = backgroundColor
    }
  }, [backgroundColor])

  const saveBackgroundColor = () => {
    const updatedText = {
      ...textbox,
      bgColor: backgroundColor,
    }
    dispatch(updateText(updatedText))
    editorRef.current.focus()
    setOpenColorModal(false)
  }

  const updateFontColor = () => {
    setOpenColorModal(false)
    setChangeFont(false)
    editorRef.current.focus()
    setUndoLevelCount(0)
    if (
      changeFont &&
      editorRef &&
      editorRef.current &&
      editorRef.current.selection
    ) {
      if (editorRef.current.formatter.match('textcolor')) {
        editorRef.current.formatter.remove('textcolor')
        editorRef.current.formatter.apply('textcolor')
        setEditorContent(editorRef.current.getContent())
        setDirty(true)
      } else {
        editorRef.current.formatter.apply('textcolor')
        setEditorContent(editorRef.current.getContent())
      }
    }
  }

  const undoAction = () => {
    const quill = quillRef.current
    if (quill && quill.history?.stack?.undo?.length > 1) {
      quill.history.undo()
    }
  }

  const cancelAndCloseColorPicker = () => {
    if (!changeFont && textbox) {
      setBackgroundColor(textbox.bgColor)
      editorRef.current.focus()
    } else {
      undoAction()
      setUndoLevelCount(0)
    }
    closeColorPicker()
  }

  const closeColorPicker = () => {
    setOpenColorModal(false)
    if (setChangeFont) {
      setChangeFont(false)
    }
  }

  const toggleBold = () => {
    const quill = quillRef.current
    if (!quill) {
      return
    }

    const format = quill.getFormat()
    if (format.bold === true) {
      quill.format('bold', false)
    } else {
      quill.format('bold', true)
    }
  }

  const toggleUnderline = () => {
    const quill = quillRef.current
    if (!quill) {
      return
    }

    const format = quill.getFormat()
    if (format.underline === true) {
      quill.format('underline', false)
    } else {
      quill.format('underline', true)
    }
  }

  const toggleItalic = () => {
    const quill = quillRef.current
    if (!quill) {
      return
    }

    const format = quill.getFormat()
    if (format.italic === true) {
      quill.format('italic', false)
    } else {
      quill.format('italic', true)
    }
  }

  const toggleLink = () => {
    const quill = quillRef.current
    if (!quill || !setOpenLinkUrlPrompt) {
      return
    }

    const selection = quill.getSelection()
    if (!selection || selection.length === 0) {
      return
    }

    const currentFormat = quill.getFormat(selection)
    const existingLink =
      typeof currentFormat.link === 'string' && currentFormat.link.length > 0
        ? currentFormat.link
        : 'https://'
    const selectionSnapshot = { index: selection.index, length: selection.length }

    const restoreSelection = () => {
      quill.focus()
      quill.setSelection(selectionSnapshot)
    }

    setOpenLinkUrlPrompt({
      defaultValue: existingLink || 'https://',
      onConfirm: (url) => {
        const trimmedUrl = url.trim()
        if (trimmedUrl.length === 0) {
          return
        }
        quill.format('link', trimmedUrl)
      },
      onCancel: () => {
        restoreSelection()
        quill.format('link', false)
      },
    })
  }

  const toggleHeader1 = () => {
    const quill = quillRef.current
    if (!quill) {
      return
    }

    const format = quill.getFormat()
    if (format.header === 1) {
      quill.format('header', false)
    } else {
      quill.format('header', 1)
    }
  }

  const toggleHeader2 = () => {
    const quill = quillRef.current
    if (!quill) {
      return
    }

    const format = quill.getFormat()
    if (format.header === 2) {
      quill.format('header', false)
    } else {
      quill.format('header', 2)
    }
  }

  return (
    <>
      {textbox && parent && (
        <div
          className={`textbox ${toolbarVisible ? '' : 'hide-toolbar'}`}
          id={`textbox-${textbox.id}`}
          style={{
            backgroundColor: openColorModal ? backgroundColor : textbox.bgColor,
            fontSize: getFontSize(),
            fontColor: '#333',
            position: textbox.draggable ? 'fixed' : 'relative',
          }}
        >
          <div
            id={`toolbar-${textbox.id}`}
            style={
              textbox.draggable
                ? { position: 'fixed', bottom: textbox.h * parent.height }
                : { position: 'absolute', bottom: textbox.h * parent.height }
            }
          >
            <span className="ql-formats">
              <button
                onClick={() => toggleBold()}
                title="Lihavoitu"
                style={{ fontWeight: 'bold' }}
              >
                B
              </button>
              <button
                onClick={() => toggleUnderline()}
                title="Alleviivaus"
                style={{ textDecoration: 'underline' }}
              >
                U
              </button>
              <button
                onClick={() => toggleItalic()}
                title="Kursivoitu"
                style={{
                  fontStyle: 'italic',
                  fontFamily: 'Georgia',
                  fontSize: '15px',
                  paddingTop: '4px',
                }}
              >
                I
              </button>
            </span>

            <span className="ql-formats">
              <button
                id="ql-custom-button-h1"
                onClick={() => toggleHeader1()}
                title="Otsikko 1"
              >
                H1
              </button>
              <button
                id="ql-custom-button-h2"
                onClick={() => toggleHeader2()}
                title="Otsikko 2"
              >
                H2
              </button>
            </span>

            <span className="ql-formats">
              <button
                className="ql-list"
                value="bullet"
                title="Järjestämätön lista"
              ></button>
              <button
                className="ql-list"
                value="ordered"
                title="Järjestetty lista"
              ></button>
            </span>

            <span className="ql-formats">
              <select className="ql-align" title="Tasaus">
                <option value=""></option>
                <option value="center"></option>
                <option value="right"></option>
              </select>
              <button
                className="ql-link"
                onClick={() => toggleLink()}
                title="Lisää linkki"
              ></button>
            </span>

            <span className="ql-formats">
              <select
                className={`ql-size ${
                  topAlignFontMenu ? 'top-align-font-menu' : ''
                }`}
                title="Tekstin koko"
              >
                <option value="">Tekstikoko</option>
                <option value="small">Pieni teksti</option>
                <option value="">Keskikokoinen teksti</option>
                <option value="large">Isompi teksti</option>
                <option value="huge">Iso teksti</option>
                <option value="mega">Isoin teksti</option>
              </select>
            </span>

            <span className="ql-formats">
              <button
                id="ql-custom-button-color"
                onClick={() => changeFontColor()}
                title="Vaihda tekstin väriä"
                aria-label="Vaihda tekstin väriä"
              >
                <FontAwesomeIcon icon="font" aria-hidden="true" />
              </button>
              <button
                id="ql-custom-button-background-color"
                onClick={() => setOpenColorModal(true)}
                title="Vaihda tekstin taustaväriä"
                aria-label="Vaihda tekstin taustaväriä"
              >
                <FontAwesomeIcon icon="fill-drip" aria-hidden="true" />
              </button>
            </span>

            <span className="ql-formats">
              <button
                id="ql-custom-button-undo"
                onClick={() => undoAction()}
                title="Kumoa"
                // disabled={quillRef?.current?.history?.stack?.undo?.length > 1}
              >
                <FontAwesomeIcon icon="reply" />
              </button>
              <button
                id="ql-custom-button-empty"
                onClick={() => setOpenDeleteContentModal(true)}
                title="Tyhjennä tekstikenttä"
              >
                <FontAwesomeIcon icon={['far', 'trash-alt']} />
              </button>
            </span>
          </div>
          <div
            id={`editor-${textbox.id}`}
            ref={editorRef}
            className="quill-editor-custom-class"
            width={width}
          ></div>
          {openDeleteContentModal && (
            <DeleteItemDialogue
              openDeleteContentModal={openDeleteContentModal}
              setOpenDeleteContentModal={setOpenDeleteContentModal}
              deleteItem={emptyContents}
              textContent={[
                'Haluatko varmasti tyhjentää tekstin?',
                'Kyllä, tyhjennä',
              ]}
            />
          )}
          {openColorModal && (
            <ColorPicker
              openColorModal={openColorModal}
              setOpenColorModal={setOpenColorModal}
              changeFont={changeFont}
              textbox={textbox}
              color={changeFont ? fontColor : backgroundColor}
              setColor={changeFont ? setFontColor : setBackgroundColor}
              cancelAndCloseColorPicker={cancelAndCloseColorPicker}
              chooseColor={changeFont ? updateFontColor : saveBackgroundColor}
            />
          )}
        </div>
      )}
    </>
  )
}

TextEditor.propTypes = {
  textbox: propTypes.instanceOf(Object).isRequired,
  parent: propTypes.instanceOf(Object).isRequired,
  editorContent: propTypes.string,
  setEditorContent: propTypes.func.isRequired,
  setDirty: propTypes.func.isRequired,
  saveText: propTypes.func.isRequired,
  getFontSize: propTypes.func.isRequired,
  dragging: propTypes.bool,
  width: propTypes.number.isRequired,
  setOpenLinkUrlPrompt: propTypes.func,
  topAlignFontMenu: propTypes.bool,
}

TextEditor.defaultProps = {
  editorContent: null,
  dragging: false,
}

export default TextEditor
